import {useEffect, useState} from 'react';
import ButtonSolid from 'components/Buttons/ButtonSolid';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import Analytics from 'utils/Analytics';
import {readSeverityFromUrl} from 'surveys/osa_funnel/survey/severity';
import {readReturnToFromUrl} from 'surveys/osa_funnel/survey/returnTo';
import styles from './DismissSurvey.module.css';

type Option = {value: string; label: string; icon: string};

const OPTIONS: Option[] = [
  {value: 'cost', label: 'Concerned about cost', icon: '💰'},
  {value: 'side_effects', label: 'Worried about side effects', icon: '😬'},
  {value: 'wont_work', label: "Afraid it won't work for me", icon: '🤷'},
  {value: 'already_on_glp1', label: "I'm already on a GLP-1", icon: '✅'},
  {value: 'not_ready', label: 'Not ready to start medication yet', icon: '🗓️'},
  {value: 'not_right_provider', label: 'Not sure RISE is the right provider for me', icon: '❓'},
  {value: 'lifestyle_first', label: 'I want to try lifestyle changes first', icon: '🏃'},
  {value: 'injection_barrier', label: 'Injections are a barrier', icon: '💉'},
  {value: 'insurance_check', label: 'Want to check what my insurance or PCP covers first', icon: '🏥'},
  {value: 'doctor_advised', label: 'My doctor advised against it', icon: '🙅'},
];

export const DismissSurvey = () => {
  const severity = readSeverityFromUrl();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    Analytics.track('GLP-1 Funnel Dismiss Survey Viewed', {
      severity,
      exit_reason: surveyInstance.getValue('grief_exit_reason'),
      exit_value: surveyInstance.getValue('grief_exit_value'),
    });
  }, [severity]);

  const toggle = (value: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const handleSubmit = () => {
    const values = Array.from(selected);
    surveyInstance.setValue('osa_dismiss_reasons', values);
    Analytics.track('GLP-1 Funnel Dismiss Survey Submitted', {
      severity,
      exit_reason: surveyInstance.getValue('grief_exit_reason'),
      exit_value: surveyInstance.getValue('grief_exit_value'),
      dismiss_reasons: values,
    });
    window.location.assign(readReturnToFromUrl());
  };

  return (
    <div className={styles.container} data-testid="osa-dismiss-survey">
      <div className={styles.body}>
        <h2 className={styles.headline}>What held you back?</h2>
        <p className={styles.subhead}>Pick anything that applies.</p>
        <ul className={styles.options}>
          {OPTIONS.map(option => {
            const isSelected = selected.has(option.value);
            return (
              <li key={option.value}>
                <button
                  type="button"
                  className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
                  onClick={() => toggle(option.value)}
                  aria-pressed={isSelected}
                >
                  <span className={styles.icon} aria-hidden="true">
                    {option.icon}
                  </span>
                  <span className={styles.label}>{option.label}</span>
                  <span className={`${styles.checkbox} ${isSelected ? styles.checkboxOn : ''}`} aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <div className={styles.cta}>
        <ButtonSolid title="Submit" onClick={handleSubmit} disabled={selected.size === 0} />
      </div>
    </div>
  );
};

export default DismissSurvey;
